/**
 * Utilities for grouping calendar data by site.
 * Site is derived from activity → order → site or activity.site (not asset.reserved_site).
 */

import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

// Extract the Order type from GetOrdersQuery result
type OrderFromQuery = NonNullable<gqlTypes.GetOrdersQuery['orders']['data'][number]>

// Extended AssetActivityOrder with fields from the source schema
// The published package has a minimal version, but the actual API returns these fields
export interface AssetActivityOrderExtended extends gqlTypes.AssetActivityOrder {
  end?: string | null
  quantity?: number | null
  quantityPlanned?: number | null
  start?: string | null
}

export interface ActivitySite {
  id: string
  name: string
}

export interface ActivityWithSite {
  id: string
  kind: string
  start: string
  end: string
  order_id?: string | null
  order?: AssetActivityOrderExtended | null
  site?: ActivitySite | null
  source_site?: ActivitySite | null
  destination_site?: ActivitySite | null
  quantity?: number | null
  assets?: Array<{ id: string; name?: string }> | null
}

export interface AssetCalendarData {
  asset: { id: string; name: string; [key: string]: unknown }
  activities: ActivityWithSite[]
}

export interface SuggestedFulfilment {
  site_id: string
  order_id: string
  suggested_start_date: string
  suggested_end_date: string
  suggested_quantity: number
  confidence: number
  days_until: number
  forecast_method: string
  projected_level_at_start: number
}

export interface ConsumptionPoint {
  date: string
  consumption: number
  is_forecast: boolean
}

export interface SuggestedFulfilmentsResponse {
  site_id: string
  suggestions: SuggestedFulfilment[]
  consumption_timeline: ConsumptionPoint[]
  has_iot_data: boolean
  estimated_current_level: number
}

export interface SiteGroup {
  site: ActivitySite | null
  assets: AssetCalendarData[]
  orders: AssetActivityOrderExtended[]
  fulfilments: ActivityWithSite[]
  suggestedFulfilments: SuggestedFulfilment[]
  consumptionTimeline: ConsumptionPoint[]
  hasIotData: boolean
  estimatedCurrentLevel: number
  isExpanded: boolean
}

const UNASSIGNED_SITE_ID = "__unassigned__"
const UNASSIGNED_SITE: ActivitySite = { id: UNASSIGNED_SITE_ID, name: "Unassigned" }

/**
 * Extract site from an activity using the correct data model:
 * - Fulfilment: activity.site (delivery site)
 * - Movement: activity.destination_site ?? activity.site
 * - Other: activity.site
 */
export function getSiteFromActivity(activity: ActivityWithSite): ActivitySite | null {
  if (activity.kind === "fulfilment") {
    return activity.site ?? null
  }
  if (activity.kind === "movement") {
    return activity.destination_site ?? activity.site ?? null
  }
  return activity.site ?? null
}

/**
 * Get a stable site key for grouping (handles null as "Unassigned").
 */
export function getSiteKey(site: ActivitySite | null): string {
  return site?.id ?? UNASSIGNED_SITE_ID
}

/**
 * Get display site for a key (null maps to Unassigned).
 */
export function getSiteFromKey(key: string): ActivitySite | null {
  return key === UNASSIGNED_SITE_ID ? UNASSIGNED_SITE : { id: key, name: key }
}

/**
 * Aggregate orders from fulfilment activities within a set of assets.
 * Each order's planned quantity is summed from fulfilment activities.
 */
function aggregateOrdersFromActivities(
  assets: AssetCalendarData[],
  siteFilter: (activity: ActivityWithSite) => boolean,
): AssetActivityOrderExtended[] {
  const orderMap = new Map<string, AssetActivityOrderExtended>()

  for (const { activities } of assets) {
    for (const activity of activities) {
      if (activity.kind !== "fulfilment" || !activity.order_id || !activity.order) continue
      if (!siteFilter(activity)) continue

      const existing = orderMap.get(activity.order_id)
      const order = activity.order

      if (
        !existing &&
        order?.quantityPlanned !== undefined &&
        order?.quantity !== undefined &&
        order?.start !== undefined &&
        order?.name !== undefined
      ) {
        orderMap.set(activity.order_id, order)
      }
    }
  }

  return Array.from(orderMap.values())
}

/**
 * Aggregate fulfilment activities within a set of assets.
 * Returns all fulfilment activities that match the site filter.
 */
function aggregateFulfilmentsFromActivities(
  assets: AssetCalendarData[],
  siteFilter: (activity: ActivityWithSite) => boolean,
): ActivityWithSite[] {
  const fulfilments: ActivityWithSite[] = []

  for (const assetData of assets) {
    for (const activity of assetData.activities) {
      if (activity.kind !== "fulfilment") continue
      if (!siteFilter(activity)) continue

      // Inject the parent asset into the activity's assets array if it's empty
      const activityWithAsset: ActivityWithSite = {
        ...activity,
        assets: activity.assets && activity.assets.length > 0 
          ? activity.assets 
          : [{ id: assetData.asset.id, name: assetData.asset.name }]
      }

      fulfilments.push(activityWithAsset)
    }
  }

  return fulfilments
}

/**
 * Convert an Order from getOrders to AssetActivityOrder format for display.
 */
function convertOrderToActivityOrder(order: OrderFromQuery): AssetActivityOrderExtended {
  return {
    __typename: 'AssetActivityOrder',
    id: order.id,
    name: order.name,
    start: order.start ?? undefined,
    end: order.end ?? undefined,
    quantity: order.quantity ?? undefined,
    quantityPlanned: order.quantity_fulfilled ?? 0,
  }
}

/**
 * Merge external orders (from getOrders) with activity-derived orders for a site.
 * External orders are filtered by site ID and converted to AssetActivityOrder format.
 * Deduplicates by order ID (activity-derived orders take precedence).
 */
function mergeExternalOrders(
  activityOrders: AssetActivityOrderExtended[],
  externalOrders: OrderFromQuery[],
  siteId: string,
): AssetActivityOrderExtended[] {
  const orderMap = new Map<string, AssetActivityOrderExtended>()
  
  // First, add all activity-derived orders
  for (const order of activityOrders) {
    orderMap.set(order.id, order)
  }
  
  // Then, add external orders that match this site (if not already present)
  for (const order of externalOrders) {
    if (orderMap.has(order.id)) continue
    
    // Check if this order is associated with this site
    const hasSite = order.sites?.some(s => s.site.id === siteId)
    if (!hasSite) continue
    
    // Convert and add the order
    const activityOrder = convertOrderToActivityOrder(order)
    
    // Only add if it has required fields
    if (activityOrder.start && activityOrder.name) {
      orderMap.set(order.id, activityOrder)
    }
  }
  
  return Array.from(orderMap.values())
}

/**
 * Check if a site group has any fulfilments or orders.
 * Sites with only fills/movements (no fulfilments or orders) are excluded from the view.
 */
export function hasFulfilmentsOrOrders(siteGroup: SiteGroup): boolean {
  return siteGroup.fulfilments.length > 0 || siteGroup.orders.length > 0
}

/**
 * Filter site groups to only include those with fulfilments or orders.
 * Preserves order and does not modify activities within qualifying groups.
 */
export function filterSiteGroupsByFulfilmentOrOrders(
  siteGroups: SiteGroup[]
): SiteGroup[] {
  return siteGroups.filter(sg => hasFulfilmentsOrOrders(sg))
}

/**
 * Transform flat asset calendar data into site groups.
 * Assets are assigned to sites based on their activities' sites.
 * An asset can appear in multiple site groups if it has activities at different sites.
 */
export function transformToSiteGroups(
  data: AssetCalendarData[],
  defaultExpanded = true,
  visibleTimeStart: number,
  visibleTimeEnd: number,
  suggestedFulfilments: SuggestedFulfilment[] = [],
  externalOrders: OrderFromQuery[] = [],
  siteResponses: Map<string, SuggestedFulfilmentsResponse> = new Map(),
): SiteGroup[] {
  console.log("transformToSiteGroups", {
    visibleTimeStart,
    visibleTimeEnd,
  })
  const siteToAssets = new Map<string, AssetCalendarData[]>()
  const siteToMeta = new Map<string, ActivitySite | null>()

  for (const item of data) {
    const activitySites = new Set<string>()

    for (const activity of item.activities) {
      const startTime = new Date(activity.start).getTime()
      const endTime = new Date(activity.end).getTime()
      // Check if activity is outside the visible time range
      // An activity can have a start time before the visible time range and an end time after the visible time range
      // if (startTime < visibleTimeStart || endTime > visibleTimeEnd) continue
      if (endTime < visibleTimeStart || startTime > visibleTimeEnd) continue
      const site = getSiteFromActivity(activity)
      const key = getSiteKey(site)
      activitySites.add(key)

      if (!siteToMeta.has(key)) {
        siteToMeta.set(key, site ?? UNASSIGNED_SITE)
      }
    }

    // If asset has no activities with a site, put in Unassigned
    if (activitySites.size === 0) {
      activitySites.add(UNASSIGNED_SITE_ID)
      if (!siteToMeta.has(UNASSIGNED_SITE_ID)) {
        siteToMeta.set(UNASSIGNED_SITE_ID, UNASSIGNED_SITE)
      }
    }

    for (const siteKey of Array.from(activitySites)) {
      if (!siteToAssets.has(siteKey)) {
        siteToAssets.set(siteKey, [])
      }
      siteToAssets.get(siteKey)!.push(item)
    }
  }

  // Add sites from external orders that don't have activities
  for (const order of externalOrders) {
    if (!order.sites) continue
    
    for (const orderSite of order.sites) {
      const siteId = orderSite.site.id
      const siteName = orderSite.site.name
      
      if (!siteToMeta.has(siteId)) {
        siteToMeta.set(siteId, { id: siteId, name: siteName })
      }
      
      // Initialise empty assets array for this site if it doesn't exist
      if (!siteToAssets.has(siteId)) {
        siteToAssets.set(siteId, [])
      }
    }
  }

  const siteKeys = Array.from(siteToAssets.keys()).sort((a, b) => {
    if (a === UNASSIGNED_SITE_ID) return 1
    if (b === UNASSIGNED_SITE_ID) return -1
    const nameA = siteToMeta.get(a)?.name ?? ""
    const nameB = siteToMeta.get(b)?.name ?? ""
    return nameA.localeCompare(nameB)
  })

  return siteKeys
    .map((key) => {
      const assets = siteToAssets.get(key) ?? []
      const site = siteToMeta.get(key) ?? null
      const siteFilter = (a: ActivityWithSite) => getSiteKey(getSiteFromActivity(a)) === key
      const activityOrders = aggregateOrdersFromActivities(assets, siteFilter)
      
      // Merge external orders for this site
      const orders = site && site.id !== UNASSIGNED_SITE_ID
        ? mergeExternalOrders(activityOrders, externalOrders, site.id)
        : activityOrders
      
      const fulfilments = aggregateFulfilmentsFromActivities(assets, siteFilter)
      
      // Filter suggested fulfilments for this site
      const siteSuggestions = site 
        ? suggestedFulfilments.filter(sf => sf.site_id === site.id)
        : []
      
      // Get consumption data for this site
      const siteResponse = site ? siteResponses.get(site.id) : undefined
      const consumptionTimeline = siteResponse?.consumption_timeline ?? []
      const hasIotData = siteResponse?.has_iot_data ?? false
      const estimatedCurrentLevel = siteResponse?.estimated_current_level ?? 300

      return {
        site: key === UNASSIGNED_SITE_ID ? UNASSIGNED_SITE : site,
        assets,
        orders,
        fulfilments,
        suggestedFulfilments: siteSuggestions,
        consumptionTimeline,
        hasIotData,
        estimatedCurrentLevel,
        isExpanded: defaultExpanded,
      }
    })
    .filter((sg) => sg.site !== UNASSIGNED_SITE)
    .filter((sg) => hasFulfilmentsOrOrders(sg))
}
